import { TRPCClientError } from '@trpc/client'
import type { inferProcedureInput, inferProcedureOutput } from '@trpc/server'
import type { AppRouter } from '@viapeople/api'
import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { trpcClient } from '../../../../utils/trpc'

type InitializeData = inferProcedureOutput<AppRouter['client']['dealTeamAggregation']['initialize']>
type PerformanceTrendData = InitializeData['summary']['performanceTrend']
type SearchCyclesData = inferProcedureOutput<AppRouter['client']['dealTeamAggregation']['searchCycles']>
type SearchParticipantsData = inferProcedureOutput<AppRouter['client']['dealTeamAggregation']['searchParticipants']>
type WorkflowData = inferProcedureOutput<AppRouter['client']['dealTeamAggregation']['getWorkflowOverview']>
type ParticipantProfileData = inferProcedureOutput<AppRouter['client']['dealTeamAggregation']['getParticipantProfile']>
type ParticipantPerformanceData = inferProcedureOutput<
	AppRouter['client']['dealTeamAggregation']['getParticipantPerformanceByQuestion']
>
type DealDetailsData = inferProcedureOutput<AppRouter['client']['dealTeamAggregation']['getDealDetails']>
type ParticipantDealReportData = inferProcedureOutput<
	AppRouter['client']['dealTeamAggregation']['getParticipantDealReport']
>
type CycleInsightsData = inferProcedureOutput<AppRouter['client']['dealTeamAggregation']['getCycleInsights']>
type DistributionBuckets = inferProcedureOutput<
	AppRouter['client']['dealTeamAggregation']['getDistributionParticipants']
>
type CycleTemplateItem = InitializeData['templates'][number]
type CycleSearchInput = inferProcedureInput<AppRouter['client']['dealTeamAggregation']['searchCycles']>
type ParticipantSearchInput = inferProcedureInput<AppRouter['client']['dealTeamAggregation']['searchParticipants']>

export interface AggregationStore {
	organizationId: string | null
	summary: InitializeData['summary'] | null
	performanceTrend: PerformanceTrendData | null
	cycles: SearchCyclesData | null
	participants: SearchParticipantsData | null
	distributionBuckets: DistributionBuckets | null
	workflow: WorkflowData | null
	participantProfile: ParticipantProfileData
	participantPerformanceByQuestion: ParticipantPerformanceData | null
	participantSelectedTemplateId: string | null
	isParticipantPerformanceLoading: boolean
	isExportingParticipant: boolean
	isExportingParticipantProfilePdf: boolean
	isExportingDealTeamParticipantsCsv: boolean
	isExportingDealTeamParticipantDealsCsv: boolean
	isExportingDealTeamParticipantsReportsZip: boolean
	dealDetails: DealDetailsData
	isDealDetailsLoading: boolean
	dealDistributionBuckets: DistributionBuckets | null
	isDealDistributionBucketsLoading: boolean
	participantDealReport: ParticipantDealReportData | null
	isParticipantDealReportLoading: boolean
	isExportingParticipantDealReportPdf: boolean
	isExportingDealReportPdf: boolean
	isExportingDealParticipantsCsv: boolean
	isExportingDealParticipantsZip: boolean
	cycleTemplates: CycleTemplateItem[]
	selectedTemplateId: string | null
	cycleInsights: CycleInsightsData | null
	isCycleInsightsLoading: boolean
	selectedCycleId: string | null
	selectedParticipantId: string | null
	cycleSearchQuery: Partial<CycleSearchInput>
	participantSearchQuery: Partial<ParticipantSearchInput>
	activeTab: 'deals' | 'participants'
	isLoading: boolean
	isWorkflowLoading: boolean
	isParticipantProfileLoading: boolean
	isExporting: boolean
	/** Fatal error from initialization — renders full-page error block. */
	error: string | null
	/** Type of fatal error: 'forbidden' for access/permission errors, 'generic' for all others. */
	errorType: 'forbidden' | 'generic' | null
	/** Transient error from search/export/workflow operations — shown as dismissable inline banner. */
	operationError: string | null
	cycleSearchTimeout: NodeJS.Timeout | null
	participantSearchTimeout: NodeJS.Timeout | null

	initialize: (organizationId: string) => Promise<void>
	searchCycles: () => Promise<void>
	searchParticipants: () => Promise<void>
	setCycleSearchQuery: (query: Partial<CycleSearchInput>) => void
	setParticipantSearchQuery: (query: Partial<ParticipantSearchInput>) => void
	setActiveTab: (tab: 'deals' | 'participants') => void
	selectCycle: (cycleId: string) => void
	clearSelectedCycle: () => void
	selectParticipant: (userId: string) => void
	clearSelectedParticipant: () => void
	loadWorkflow: (cycleId: string) => Promise<void>
	loadDealDetails: (cycleId: string) => Promise<void>
	clearDealDetails: () => void
	loadDealDistributionParticipants: (cycleId: string) => Promise<void>
	loadParticipantDealReport: (cycleId: string, userId: string) => Promise<void>
	clearParticipantDealReport: () => void
	exportParticipantDealReportPdf: (cycleId: string, userId: string, view?: 'admin' | 'employee') => Promise<void>
	exportDealReportPdf: (cycleId: string) => Promise<void>
	exportDealParticipantsCsv: (cycleId: string, userIds?: string[]) => Promise<void>
	exportDealParticipantsReportsZip: (
		cycleId: string,
		options?: { userIds?: string[]; view?: 'admin' | 'employee' },
	) => Promise<void>
	loadParticipantProfile: (userId: string) => Promise<void>
	loadParticipantPerformanceByQuestion: (userId: string, templateId: string) => Promise<void>
	selectParticipantTemplate: (templateId: string) => void
	exportCycles: (cycleIds?: string[]) => Promise<void>
	exportParticipantProfile: (userId: string) => Promise<void>
	exportParticipantProfilePdf: (userId: string) => Promise<void>
	exportDealTeamParticipantsCsv: (userIds?: string[]) => Promise<void>
	exportDealTeamParticipantDealsCsv: (userIds?: string[]) => Promise<void>
	exportDealTeamParticipantsReportsZip: (options?: { userIds?: string[]; view?: 'admin' | 'employee' }) => Promise<void>
	selectTemplate: (templateId: string) => void
	loadCycleInsights: () => Promise<void>
	loadDistributionParticipants: () => Promise<void>
	setError: (error: string | null) => void
	setErrorType: (errorType: 'forbidden' | 'generic' | null) => void
	setOperationError: (error: string | null) => void
}

export const useAggregationStore = create<AggregationStore>()(
	devtools(
		(set, get) => ({
			organizationId: null,
			summary: null,
			performanceTrend: null,
			cycles: null,
			participants: null,
			distributionBuckets: null,
			workflow: null,
			participantProfile: null,
			participantPerformanceByQuestion: null,
			participantSelectedTemplateId: null,
			isParticipantPerformanceLoading: false,
			isExportingParticipant: false,
			isExportingParticipantProfilePdf: false,
			isExportingDealTeamParticipantsCsv: false,
			isExportingDealTeamParticipantDealsCsv: false,
			isExportingDealTeamParticipantsReportsZip: false,
			dealDetails: null,
			isDealDetailsLoading: false,
			dealDistributionBuckets: null,
			isDealDistributionBucketsLoading: false,
			participantDealReport: null,
			isParticipantDealReportLoading: false,
			isExportingParticipantDealReportPdf: false,
			isExportingDealReportPdf: false,
			isExportingDealParticipantsCsv: false,
			isExportingDealParticipantsZip: false,
			cycleTemplates: [],
			selectedTemplateId: null,
			cycleInsights: null,
			isCycleInsightsLoading: false,
			selectedCycleId: null,
			selectedParticipantId: null,
			cycleSearchQuery: {},
			participantSearchQuery: {},
			activeTab: 'deals',
			isLoading: false,
			isWorkflowLoading: false,
			isParticipantProfileLoading: false,
			isExporting: false,
			error: null,
			errorType: null,
			operationError: null,
			cycleSearchTimeout: null,
			participantSearchTimeout: null,

			initialize: async (organizationId: string) => {
				if (get().organizationId === organizationId && get().summary) {
					if (!get().distributionBuckets) get().loadDistributionParticipants()
					return
				}
				set({ isLoading: true, organizationId, error: null, errorType: null })
				try {
					const data = await trpcClient.client.dealTeamAggregation.initialize.query({ organizationId })
					const firstTemplateId = data.templates[0]?.templateId ?? null
					set({
						summary: data.summary,
						performanceTrend: data.summary.performanceTrend,
						cycles: data.cycles,
						cycleTemplates: data.templates,
						selectedTemplateId: firstTemplateId,
						workflow: null,
						participantProfile: null,
						participantPerformanceByQuestion: null,
						participantSelectedTemplateId: null,
						selectedCycleId: null,
						selectedParticipantId: null,
						cycleSearchQuery: { organizationId, page: 1, perPage: 20, sortBy: 'createdAt', order: 'desc' },
						participantSearchQuery: { organizationId, page: 1, perPage: 20 },
						isLoading: false,
					})
					if (firstTemplateId) {
						get().loadCycleInsights()
					}
					get().loadDistributionParticipants()
				} catch (error) {
					const isForbidden = error instanceof TRPCClientError && error.data?.code === 'FORBIDDEN'
					set({
						error: error instanceof Error ? error.message : 'Failed to load aggregation portal',
						errorType: isForbidden ? 'forbidden' : 'generic',
						isLoading: false,
					})
				}
			},

			searchCycles: async () => {
				const { organizationId, cycleSearchQuery } = get()
				if (!organizationId) return
				try {
					const result = await trpcClient.client.dealTeamAggregation.searchCycles.query({
						organizationId,
						page: 1,
						perPage: 20,
						sortBy: 'createdAt',
						order: 'desc',
						...cycleSearchQuery,
					} as CycleSearchInput)
					set({ cycles: result, operationError: null })
				} catch (error) {
					set({ operationError: error instanceof Error ? error.message : 'Failed to search cycles' })
				}
			},

			searchParticipants: async () => {
				const { organizationId, participantSearchQuery } = get()
				if (!organizationId) return
				try {
					const result = await trpcClient.client.dealTeamAggregation.searchParticipants.query({
						organizationId,
						page: 1,
						perPage: 20,
						...participantSearchQuery,
					} as ParticipantSearchInput)
					set({ participants: result, operationError: null })
				} catch (error) {
					set({ operationError: error instanceof Error ? error.message : 'Failed to search participants' })
				}
			},

			setCycleSearchQuery: (query) => {
				const { cycleSearchTimeout, searchCycles } = get()
				if (cycleSearchTimeout) clearTimeout(cycleSearchTimeout)
				set({
					cycleSearchQuery: { ...get().cycleSearchQuery, ...query },
					cycleSearchTimeout: setTimeout(searchCycles, 300),
				})
			},

			setParticipantSearchQuery: (query) => {
				const { participantSearchTimeout, searchParticipants } = get()
				if (participantSearchTimeout) clearTimeout(participantSearchTimeout)
				set({
					participantSearchQuery: { ...get().participantSearchQuery, ...query },
					participantSearchTimeout: setTimeout(searchParticipants, 300),
				})
			},

			setActiveTab: (tab) => set({ activeTab: tab }),

			selectCycle: (cycleId: string) => {
				set({ selectedCycleId: cycleId, workflow: null })
			},

			clearSelectedCycle: () => {
				set({ selectedCycleId: null, workflow: null })
			},

			selectParticipant: (userId: string) => {
				set({ selectedParticipantId: userId, participantProfile: null })
			},

			clearSelectedParticipant: () => {
				set({
					selectedParticipantId: null,
					participantProfile: null,
					participantPerformanceByQuestion: null,
					participantSelectedTemplateId: null,
				})
			},

			loadWorkflow: async (cycleId: string) => {
				const { organizationId } = get()
				if (!organizationId) return
				set({ isWorkflowLoading: true })
				try {
					const result = await trpcClient.client.dealTeamAggregation.getWorkflowOverview.query({
						organizationId,
						cycleId,
					})
					set({ workflow: result, isWorkflowLoading: false, operationError: null })
				} catch (error) {
					set({
						operationError: error instanceof Error ? error.message : 'Failed to load workflow',
						isWorkflowLoading: false,
					})
				}
			},

			loadDealDetails: async (cycleId: string) => {
				const { organizationId } = get()
				if (!organizationId) return
				set({ isDealDetailsLoading: true, dealDetails: null })
				try {
					const result = await trpcClient.client.dealTeamAggregation.getDealDetails.query({
						organizationId,
						cycleId,
					})
					set({ dealDetails: result, isDealDetailsLoading: false, operationError: null })
					get().loadDealDistributionParticipants(cycleId)
				} catch (error) {
					set({
						operationError: error instanceof Error ? error.message : 'Failed to load deal details',
						isDealDetailsLoading: false,
					})
				}
			},

			clearDealDetails: () =>
				set({
					dealDetails: null,
					isDealDetailsLoading: false,
					dealDistributionBuckets: null,
					isDealDistributionBucketsLoading: false,
				}),

			loadDealDistributionParticipants: async (cycleId: string) => {
				const { organizationId } = get()
				if (!organizationId) return
				set({ isDealDistributionBucketsLoading: true })
				try {
					const result = await trpcClient.client.dealTeamAggregation.getDealDistributionParticipants.query({
						organizationId,
						cycleId,
						perBucket: 6,
					})
					set({ dealDistributionBuckets: result, isDealDistributionBucketsLoading: false })
				} catch (err) {
					console.error('[aggregationStore] loadDealDistributionParticipants failed:', err)
					set({ isDealDistributionBucketsLoading: false })
				}
			},

			loadParticipantDealReport: async (cycleId: string, userId: string) => {
				const { organizationId } = get()
				if (!organizationId) return
				set({ isParticipantDealReportLoading: true, participantDealReport: null })
				try {
					const result = await trpcClient.client.dealTeamAggregation.getParticipantDealReport.query({
						organizationId,
						cycleId,
						userId,
					})
					set({ participantDealReport: result, isParticipantDealReportLoading: false, operationError: null })
				} catch (error) {
					set({
						operationError: error instanceof Error ? error.message : 'Failed to load participant deal report',
						isParticipantDealReportLoading: false,
					})
				}
			},

			clearParticipantDealReport: () => set({ participantDealReport: null, isParticipantDealReportLoading: false }),

			exportParticipantDealReportPdf: async (cycleId: string, userId: string, view = 'admin') => {
				const { organizationId } = get()
				if (!organizationId) return
				set({ isExportingParticipantDealReportPdf: true })
				try {
					const result = await trpcClient.client.dealTeamAggregation.exportParticipantDealReportPdf.mutate({
						organizationId,
						cycleId,
						userId,
						view,
					})
					const binary = atob(result.pdfBase64)
					const bytes = new Uint8Array(binary.length)
					for (let i = 0; i < binary.length; i++) {
						bytes[i] = binary.charCodeAt(i)
					}
					const blob = new Blob([bytes], { type: 'application/pdf' })
					const url = URL.createObjectURL(blob)
					const link = document.createElement('a')
					link.href = url
					link.download = result.filename
					document.body.appendChild(link)
					link.click()
					document.body.removeChild(link)
					setTimeout(() => URL.revokeObjectURL(url), 100)
					set({ isExportingParticipantDealReportPdf: false, operationError: null })
				} catch (error) {
					set({
						operationError: error instanceof Error ? error.message : 'Failed to export deal report PDF',
						isExportingParticipantDealReportPdf: false,
					})
				}
			},

			exportDealReportPdf: async (cycleId: string) => {
				const { organizationId } = get()
				if (!organizationId) return
				set({ isExportingDealReportPdf: true })
				try {
					const result = await trpcClient.client.dealTeamAggregation.exportDealReportPdf.mutate({
						organizationId,
						cycleId,
					})
					const binary = atob(result.pdfBase64)
					const bytes = new Uint8Array(binary.length)
					for (let i = 0; i < binary.length; i++) {
						bytes[i] = binary.charCodeAt(i)
					}
					const blob = new Blob([bytes], { type: 'application/pdf' })
					const url = URL.createObjectURL(blob)
					const link = document.createElement('a')
					link.href = url
					link.download = result.filename
					document.body.appendChild(link)
					link.click()
					document.body.removeChild(link)
					setTimeout(() => URL.revokeObjectURL(url), 100)
					set({ isExportingDealReportPdf: false, operationError: null })
				} catch (error) {
					set({
						operationError: error instanceof Error ? error.message : 'Failed to export deal report PDF',
						isExportingDealReportPdf: false,
					})
				}
			},

			exportDealParticipantsCsv: async (cycleId: string, userIds?: string[]) => {
				const { organizationId } = get()
				if (!organizationId) return
				set({ isExportingDealParticipantsCsv: true })
				try {
					const result = await trpcClient.client.dealTeamAggregation.exportDealParticipantsCsv.mutate({
						organizationId,
						cycleId,
						userIds: userIds && userIds.length > 0 ? userIds : undefined,
					})
					const blob = new Blob([result.csv], { type: 'text/csv;charset=utf-8;' })
					const url = URL.createObjectURL(blob)
					const link = document.createElement('a')
					link.href = url
					link.download = result.filename
					document.body.appendChild(link)
					link.click()
					document.body.removeChild(link)
					setTimeout(() => URL.revokeObjectURL(url), 100)
					set({ isExportingDealParticipantsCsv: false, operationError: null })
				} catch (error) {
					set({
						operationError: error instanceof Error ? error.message : 'Failed to export participants CSV',
						isExportingDealParticipantsCsv: false,
					})
				}
			},

			exportDealParticipantsReportsZip: async (
				cycleId: string,
				options: { userIds?: string[]; view?: 'admin' | 'employee' } = {},
			) => {
				const { organizationId } = get()
				if (!organizationId) return
				const { userIds, view = 'admin' } = options
				set({ isExportingDealParticipantsZip: true })
				try {
					const result = await trpcClient.client.dealTeamAggregation.exportDealParticipantsReportsZip.mutate({
						organizationId,
						cycleId,
						view,
						userIds: userIds && userIds.length > 0 ? userIds : undefined,
					})
					const binary = atob(result.zipBase64)
					const bytes = new Uint8Array(binary.length)
					for (let i = 0; i < binary.length; i++) {
						bytes[i] = binary.charCodeAt(i)
					}
					const blob = new Blob([bytes], { type: 'application/zip' })
					const url = URL.createObjectURL(blob)
					const link = document.createElement('a')
					link.href = url
					link.download = result.filename
					document.body.appendChild(link)
					link.click()
					document.body.removeChild(link)
					setTimeout(() => URL.revokeObjectURL(url), 100)
					set({ isExportingDealParticipantsZip: false, operationError: null })
				} catch (error) {
					set({
						operationError: error instanceof Error ? error.message : 'Failed to export participants reports ZIP',
						isExportingDealParticipantsZip: false,
					})
				}
			},

			loadParticipantProfile: async (userId: string) => {
				const { organizationId } = get()
				if (!organizationId) return
				set({ isParticipantProfileLoading: true })
				try {
					const result = await trpcClient.client.dealTeamAggregation.getParticipantProfile.query({
						organizationId,
						userId,
					})
					set({
						participantProfile: result,
						isParticipantProfileLoading: false,
						operationError: null,
					})
				} catch (error) {
					set({
						operationError: error instanceof Error ? error.message : 'Failed to load participant profile',
						isParticipantProfileLoading: false,
					})
				}
			},

			loadParticipantPerformanceByQuestion: async (userId: string, templateId: string) => {
				const { organizationId } = get()
				if (!organizationId) return
				set({ isParticipantPerformanceLoading: true, participantSelectedTemplateId: templateId })
				try {
					const result = await trpcClient.client.dealTeamAggregation.getParticipantPerformanceByQuestion.query({
						organizationId,
						userId,
						cycleTemplateId: templateId,
					})
					if (get().selectedParticipantId !== userId || get().participantSelectedTemplateId !== templateId) {
						return
					}
					set({
						participantPerformanceByQuestion: result,
						isParticipantPerformanceLoading: false,
						operationError: null,
					})
				} catch (error) {
					if (get().selectedParticipantId !== userId || get().participantSelectedTemplateId !== templateId) {
						return
					}
					set({
						operationError:
							error instanceof Error ? error.message : 'Failed to load participant performance by question',
						isParticipantPerformanceLoading: false,
					})
				}
			},

			selectParticipantTemplate: (templateId: string) => {
				const { selectedParticipantId, loadParticipantPerformanceByQuestion } = get()
				if (!selectedParticipantId) return
				loadParticipantPerformanceByQuestion(selectedParticipantId, templateId)
			},

			exportCycles: async (cycleIds?: string[]) => {
				const { organizationId, cycleSearchQuery } = get()
				if (!organizationId) return
				set({ isExporting: true })
				try {
					const result = await trpcClient.client.dealTeamAggregation.exportCycles.mutate({
						organizationId,
						search: cycleSearchQuery.search,
						status: cycleSearchQuery.status,
						source: cycleSearchQuery.source,
						closeDateFrom: cycleSearchQuery.closeDateFrom,
						closeDateTo: cycleSearchQuery.closeDateTo,
						cycleIds: cycleIds?.length ? cycleIds : undefined,
					})
					const blob = new Blob([result.csv], { type: 'text/csv;charset=utf-8;' })
					const url = URL.createObjectURL(blob)
					const link = document.createElement('a')
					link.href = url
					link.download = result.filename
					document.body.appendChild(link)
					link.click()
					document.body.removeChild(link)
					setTimeout(() => URL.revokeObjectURL(url), 100)
					set({ isExporting: false, operationError: null })
				} catch (error) {
					set({
						operationError: error instanceof Error ? error.message : 'Failed to export cycles',
						isExporting: false,
					})
				}
			},

			exportParticipantProfile: async (userId: string) => {
				const { organizationId, participantSelectedTemplateId } = get()
				if (!organizationId) return
				set({ isExportingParticipant: true })
				try {
					const result = await trpcClient.client.dealTeamAggregation.exportParticipantProfile.mutate({
						organizationId,
						userId,
						cycleTemplateId: participantSelectedTemplateId ?? undefined,
					})
					const blob = new Blob([result.csv], { type: 'text/csv;charset=utf-8;' })
					const url = URL.createObjectURL(blob)
					const link = document.createElement('a')
					link.href = url
					link.download = result.filename
					document.body.appendChild(link)
					link.click()
					document.body.removeChild(link)
					setTimeout(() => URL.revokeObjectURL(url), 100)
					set({ isExportingParticipant: false, operationError: null })
				} catch (error) {
					set({
						operationError: error instanceof Error ? error.message : 'Failed to export participant profile',
						isExportingParticipant: false,
					})
				}
			},

			exportParticipantProfilePdf: async (userId: string) => {
				const { organizationId, participantSelectedTemplateId } = get()
				if (!organizationId) return
				set({ isExportingParticipantProfilePdf: true })
				try {
					const result = await trpcClient.client.dealTeamAggregation.exportParticipantProfilePdf.mutate({
						organizationId,
						userId,
						cycleTemplateId: participantSelectedTemplateId ?? undefined,
					})
					const binary = atob(result.pdfBase64)
					const bytes = new Uint8Array(binary.length)
					for (let i = 0; i < binary.length; i++) {
						bytes[i] = binary.charCodeAt(i)
					}
					const blob = new Blob([bytes], { type: 'application/pdf' })
					const url = URL.createObjectURL(blob)
					const link = document.createElement('a')
					link.href = url
					link.download = result.filename
					document.body.appendChild(link)
					link.click()
					document.body.removeChild(link)
					setTimeout(() => URL.revokeObjectURL(url), 100)
					set({ isExportingParticipantProfilePdf: false, operationError: null })
				} catch (error) {
					set({
						operationError: error instanceof Error ? error.message : 'Failed to export profile PDF',
						isExportingParticipantProfilePdf: false,
					})
				}
			},

			exportDealTeamParticipantsCsv: async (userIds?: string[]) => {
				const { organizationId } = get()
				if (!organizationId) return
				set({ isExportingDealTeamParticipantsCsv: true })
				try {
					const result = await trpcClient.client.dealTeamAggregation.exportDealTeamParticipantsCsv.mutate({
						organizationId,
						userIds: userIds && userIds.length > 0 ? userIds : undefined,
					})
					const blob = new Blob([result.csv], { type: 'text/csv;charset=utf-8;' })
					const url = URL.createObjectURL(blob)
					const link = document.createElement('a')
					link.href = url
					link.download = result.filename
					document.body.appendChild(link)
					link.click()
					document.body.removeChild(link)
					setTimeout(() => URL.revokeObjectURL(url), 100)
					set({ isExportingDealTeamParticipantsCsv: false, operationError: null })
				} catch (error) {
					set({
						operationError: error instanceof Error ? error.message : 'Failed to export participants CSV',
						isExportingDealTeamParticipantsCsv: false,
					})
				}
			},

			exportDealTeamParticipantDealsCsv: async (userIds?: string[]) => {
				const { organizationId } = get()
				if (!organizationId) return
				set({ isExportingDealTeamParticipantDealsCsv: true })
				try {
					const result = await trpcClient.client.dealTeamAggregation.exportDealTeamParticipantDealsCsv.mutate({
						organizationId,
						userIds: userIds && userIds.length > 0 ? userIds : undefined,
					})
					const blob = new Blob([result.csv], { type: 'text/csv;charset=utf-8;' })
					const url = URL.createObjectURL(blob)
					const link = document.createElement('a')
					link.href = url
					link.download = result.filename
					document.body.appendChild(link)
					link.click()
					document.body.removeChild(link)
					setTimeout(() => URL.revokeObjectURL(url), 100)
					set({ isExportingDealTeamParticipantDealsCsv: false, operationError: null })
				} catch (error) {
					set({
						operationError: error instanceof Error ? error.message : 'Failed to export participant deals CSV',
						isExportingDealTeamParticipantDealsCsv: false,
					})
				}
			},

			exportDealTeamParticipantsReportsZip: async (
				options: { userIds?: string[]; view?: 'admin' | 'employee' } = {},
			) => {
				const { organizationId } = get()
				if (!organizationId) return
				const { userIds, view = 'admin' } = options
				set({ isExportingDealTeamParticipantsReportsZip: true })
				try {
					const result = await trpcClient.client.dealTeamAggregation.exportDealTeamParticipantsReportsZip.mutate({
						organizationId,
						userIds: userIds && userIds.length > 0 ? userIds : undefined,
						view,
					})
					const binary = atob(result.zipBase64)
					const bytes = new Uint8Array(binary.length)
					for (let i = 0; i < binary.length; i++) {
						bytes[i] = binary.charCodeAt(i)
					}
					const blob = new Blob([bytes], { type: 'application/zip' })
					const url = URL.createObjectURL(blob)
					const link = document.createElement('a')
					link.href = url
					link.download = result.filename
					document.body.appendChild(link)
					link.click()
					document.body.removeChild(link)
					setTimeout(() => URL.revokeObjectURL(url), 100)
					set({ isExportingDealTeamParticipantsReportsZip: false, operationError: null })
				} catch (error) {
					set({
						operationError: error instanceof Error ? error.message : 'Failed to export participants reports ZIP',
						isExportingDealTeamParticipantsReportsZip: false,
					})
				}
			},

			selectTemplate: (templateId: string) => {
				set({ selectedTemplateId: templateId })
				get().loadCycleInsights()
			},

			loadCycleInsights: async () => {
				const { organizationId, selectedTemplateId } = get()
				if (!organizationId || !selectedTemplateId) return
				const templateIdAtDispatch = selectedTemplateId
				set({ isCycleInsightsLoading: true })
				try {
					const result = await trpcClient.client.dealTeamAggregation.getCycleInsights.query({
						organizationId,
						templateId: templateIdAtDispatch,
					})
					if (get().selectedTemplateId !== templateIdAtDispatch) return
					set({ cycleInsights: result, isCycleInsightsLoading: false, operationError: null })
				} catch (error) {
					if (get().selectedTemplateId !== templateIdAtDispatch) return
					set({
						operationError: error instanceof Error ? error.message : 'Failed to load cycle insights',
						isCycleInsightsLoading: false,
					})
				}
			},

			loadDistributionParticipants: async () => {
				const { organizationId } = get()
				if (!organizationId) return
				try {
					const result = await trpcClient.client.dealTeamAggregation.getDistributionParticipants.query({
						organizationId,
						perBucket: 6,
					})
					set({ distributionBuckets: result })
				} catch (err) {
					console.error('[aggregationStore] loadDistributionParticipants failed:', err)
				}
			},

			setError: (error) => set({ error }),
			setErrorType: (errorType) => set({ errorType }),
			setOperationError: (operationError) => set({ operationError }),
		}),
		{ name: 'deal-team-aggregation-store' },
	),
)
